import jwtDecode from './jwt';
import { Cookie } from './cookie';

const SESSION_EXPIRED_FLAG = 'session_expired_message_pending';

function Auth() {
  return {
    isAuthenticated: () => {
      return !!Cookie.get('_hellobar_db_token');
    },
    getToken: () => {
      return Cookie.get('_hellobar_db_token');
    },
    getTokenData: () => {
      return JSON.parse(Cookie.get('_hellobar_db_token_data'));
    },
    signedIn: () => {
      return sessionStorage.getItem('signed_in') === 'true';
    },
    signedUp: () => {
      return !!sessionStorage.getItem('track_signed_up');
    },
    login: (token) => {
      const decodedToken = jwtDecode(token);
      Cookie.set('_hellobar_db_token_data', JSON.stringify(decodedToken));
      Cookie.set('_hellobar_db_token', token);
      Cookie.set('_hellobar_db_signed_in', true);
      sessionStorage.setItem('signed_in', 'true');
    },
    // Drops the local token only, with no server round trip or navigation — used when the
    // API rejects the current token (expired/invalidated, see DEV-895) so isAuthenticated()
    // reflects reality immediately. Without this, a stale-but-present cookie makes the sign-in
    // page think the user is already logged in and bounce them straight back into the app.
    clearSession: () => {
      Cookie.delete('_hellobar_db_token');
      Cookie.delete('_hellobar_db_token_data');
      Cookie.delete('_hellobar_db_signed_in');
      sessionStorage.removeItem('signed_in');
    },
    // Single place every API call (see api.js) reports a 401 (token expired or
    // invalidated — password change, admin "log out all sessions", DEV-895). Centralizing
    // this here means any page that calls the API gets a clean "please log in again"
    // instead of each of the ~25+ call sites needing to remember to check for it —
    // the gap that caused the Popups page and Widget publish to show a raw/generic
    // error with no way back in.
    // Returns true when it's actually navigating away, so callers (api.js) can avoid
    // handing control back to whatever component made the request — otherwise that
    // component's own error handling (an alert, a snackbar, a retry prompt) can still
    // run and flash on screen in the moment before the browser finishes navigating.
    handleUnauthorized: () => {
      if (!Cookie.get('_hellobar_db_token')) { return false; }

      Cookie.delete('_hellobar_db_token');
      Cookie.delete('_hellobar_db_token_data');
      Cookie.delete('_hellobar_db_signed_in');
      sessionStorage.removeItem('signed_in');
      try { sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1'); } catch (_) {}

      const { pathname } = window.location;
      if (pathname.startsWith('/login') || pathname.startsWith('/users')) { return false; }

      window.location.href = '/login';
      return true;
    },
    // Called once by the sign-in page on mount to show the "session expired" message
    // left behind by handleUnauthorized, without showing it again on a later visit.
    consumeSessionExpiredFlag: () => {
      let pending = false;
      try {
        pending = sessionStorage.getItem(SESSION_EXPIRED_FLAG) === '1';
        sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
      } catch (_) {}
      return pending;
    },
    logout: async () => {
      await fetch(`${process.env.REACT_APP_AUTH_HOST}/users/sign_out`, {
        method: 'DELETE',
        credentials: 'include',
      });
      Cookie.delete('_hellobar_db_token');
      Cookie.delete('_hellobar_db_token_data');
      Cookie.delete('_hellobar_db_signed_in');
      sessionStorage.removeItem('session_signed_up');
      window.location = `${process.env.REACT_APP_AUTH_HOST}/logged_out`;
    },
    loginWithPassword: ({ email, password }) => {
      const form = JSON.stringify({
        user: { email, password },
      });

      return fetch(`${process.env.REACT_APP_AUTH_HOST}/users/sign_in`, {
        method: 'POST',
        body: form,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
    },
  };
}

export const auth = Auth();
